import {
  SingleProfitCalculationApiResponse,
  Transaction,
} from '../../../../ProfitCalculations/types';
import { DeepTx, TxItem, TxType } from '../txHelpers';
import {
  fmtBalance,
  fmtDate,
  fmtPct,
  fmtPrice,
  fmtQty,
  isStopLoss,
  isTakeProfit,
  txProfitValue,
  txStableFraction,
} from './format';

const classify = (tx: Transaction): TxType => {
  if (isStopLoss(tx.transactionType)) return 'sl';
  if (isTakeProfit(tx.transactionType)) return 'tp';
  return tx.orderType === 'BUY' ? 'buy' : 'sell';
};

export const toTxItem = (tx: Transaction): TxItem => ({
  id: tx.id,
  type: classify(tx),
  orderType: tx.orderType,
  timestamp: tx.transactionTimestamp ?? tx.timestamp,
  date: fmtDate(tx.transactionTimestamp ?? tx.timestamp),
  price: fmtPrice(tx.orderPrice),
  profit: fmtPct(txProfitValue(tx)),
  stableShare: txStableFraction(tx),
  stableQty: fmtBalance(tx.stableQuantity),
  stableBal: fmtBalance(tx.balanceInUsdt),
  assetQty: fmtQty(tx.assetQuantity),
  assetBal: fmtQty(tx.balanceInAsset),
  fee: fmtBalance(tx.currentFee),
  totalFees: fmtBalance(tx.totalFee),
});

export const toDeepTx = (tx: Transaction): DeepTx => ({
  id: tx.id,
  type: classify(tx),
  orderType: tx.orderType,
  stableShare: txStableFraction(tx),
  date: fmtDate(tx.transactionTimestamp ?? tx.timestamp),
  price: fmtPrice(tx.orderPrice),
  profit: fmtPct(txProfitValue(tx)),
  stableQty: fmtBalance(tx.stableQuantity),
  stableProfit: fmtPct(tx.profitInUsdt),
  assetQty: fmtQty(tx.assetQuantity),
  assetProfit: fmtPct(tx.profitInAsset),
  stableBal: fmtBalance(tx.balanceInUsdt),
  assetBal: fmtQty(tx.balanceInAsset),
  fee: fmtBalance(tx.currentFee),
  totalFees: fmtBalance(tx.totalFee),
  extraStat: isStopLoss(tx.transactionType)
    ? { label: 'Stop loss threshold', value: fmtPct(tx.priceThreshold ?? 0) }
    : isTakeProfit(tx.transactionType)
    ? { label: 'Take profit size', value: fmtPct(tx.takeProfitSize ?? 0) }
    : null,
});

export const buildTransactionList = (
  spc: SingleProfitCalculationApiResponse,
): {
  list: TxItem[];
  deepByTxId: Record<number, DeepTx>;
  orderedIds: number[];
} => {
  const sorted = [...spc.transactions].sort(
    (a, b) =>
      (b.transactionTimestamp ?? b.timestamp) -
      (a.transactionTimestamp ?? a.timestamp),
  );

  const list = sorted.map(toTxItem);
  const deepByTxId: Record<number, DeepTx> = {};
  for (const tx of sorted) deepByTxId[tx.id] = toDeepTx(tx);

  return {
    list,
    deepByTxId,
    orderedIds: sorted.map((tx) => tx.id),
  };
};

export const avgTradeDurationMs = (
  spc: SingleProfitCalculationApiResponse,
): number => {
  if (spc.transactions.length < 2) return 0;
  const sorted = [...spc.transactions].sort(
    (a, b) =>
      (a.transactionTimestamp ?? a.timestamp) -
      (b.transactionTimestamp ?? b.timestamp),
  );
  let total = 0;
  for (let i = 1; i < sorted.length; i++) {
    total +=
      (sorted[i].transactionTimestamp ?? sorted[i].timestamp) -
      (sorted[i - 1].transactionTimestamp ?? sorted[i - 1].timestamp);
  }
  return total / (sorted.length - 1);
};

export const avgProfit = (spc: SingleProfitCalculationApiResponse): number => {
  const txs = spc.transactions;
  if (!txs.length) return 0;
  return txs.reduce((acc, tx) => acc + txProfitValue(tx), 0) / txs.length;
};
